import React, { Fragment } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, ArrowDown } from 'lucide-react';
interface MetricStairsCompactProps {
  type: 'length' | 'volume' | 'weight';
  from?: string;
  to?: string;
  value?: number;
  highlight?: string;
  showAnimation?: boolean;
  mode?: 'stimulus' | 'support';
}
const METRIC_UNITS = {
  length: ['km', 'hm', 'dam', 'm', 'dm', 'cm', 'mm'],
  volume: ['kl', 'hl', 'dal', 'l', 'dl', 'cl', 'ml', 'm3'],
  weight: ['kg', 'hg', 'dag', 'g', 'dg', 'cg', 'mg']
};
const UNIT_LABELS = {
  // Length
  km: 'kilometer',
  hm: 'hectometer',
  dam: 'decameter',
  m: 'meter',
  dm: 'decimeter',
  cm: 'centimeter',
  mm: 'millimeter',
  // Volume
  kl: 'kiloliter',
  hl: 'hectoliter',
  dal: 'decaliter',
  l: 'liter',
  dl: 'deciliter',
  cl: 'centiliter',
  ml: 'milliliter',
  m3: 'kubieke meter',
  // Weight
  kg: 'kilogram',
  hg: 'hectogram',
  dag: 'decagram',
  g: 'gram',
  dg: 'decigram',
  cg: 'centigram',
  mg: 'milligram'
};
export function MetricStairsCompact({
  type,
  from,
  to,
  value,
  highlight,
  showAnimation = true,
  mode = 'support'
}: MetricStairsCompactProps) {
  const units = METRIC_UNITS[type];
  // Defensive: an unknown `type` (e.g. Dutch alias from a malformed seed) would
  // leave `units` undefined and crash any downstream `.indexOf` / `.filter`.
  // Render nothing instead of throwing — the seed-validator will surface the
  // mismatch separately.
  if (!units || !Array.isArray(units)) return null;
  // ── STIMULUS MODE: compact unit ladder only, no calculation/tip ───────────
  if (mode === 'stimulus') {
    // If from/to conversion: show compact arrow chain
    if (from && to) {
      const fromIndex = units.indexOf(from);
      const toIndex = units.indexOf(to);
      if (fromIndex === -1 || toIndex === -1) return null;
      const isGoingDown = toIndex > fromIndex;
      const startIdx = Math.min(fromIndex, toIndex);
      const endIdx = Math.max(fromIndex, toIndex);
      const relevantUnits = units.slice(startIdx, endIdx + 1).filter(u => u !== 'm3');
      return <div className="flex flex-col items-center gap-2 py-2" data-id="element-12042">
          <div className="flex items-center gap-1.5 flex-wrap justify-center" data-id="element-12043">
            {relevantUnits.map((unit, idx) => <Fragment key={unit} data-id="element-12044">
                <div className={`px-3 py-1.5 rounded-lg font-black text-base border-2 shadow-sm
                  ${unit === from || unit === to ? 'bg-indigo-600 text-white border-indigo-700' : 'bg-white text-slate-600 border-slate-200'}`} data-id="element-12045">
                  {unit}
                </div>
                {idx < relevantUnits.length - 1 && <div className="flex flex-col items-center" data-id="element-12046">
                    <span className="text-slate-400 text-xs font-bold" data-id="element-12047">
                      {isGoingDown ? '×10' : '÷10'}
                    </span>
                    <span className="text-slate-400 text-sm" data-id="element-12048">→</span>
                  </div>}
              </Fragment>)}
          </div>
        </div>;
    }
    // Overview mode: compact vertical list
    return <div className="flex flex-col gap-1 py-2 items-center" data-id="element-12049">
        {units.filter(u => u !== 'm3').map(unit => <div key={unit} className={`px-4 py-1 rounded-lg font-bold text-sm border
            ${unit === highlight ? 'bg-indigo-600 text-white border-indigo-700 shadow-sm' : 'bg-white text-slate-600 border-slate-200'}`} data-id="element-12050">
              {unit}
            </div>)}
      </div>;
  }
  // ── SUPPORT MODE: Diagonal Staircase Layout ───────────────────────────────
  const displayUnits = units.filter(u => u !== 'm3');
  // Determine highlighted path if from/to are provided
  let fromIndex = -1;
  let toIndex = -1;
  let isGoingDown = true;
  let result: number | undefined;
  let isSpecialCase = false;
  if (from && to && value !== undefined) {
    fromIndex = displayUnits.indexOf(from);
    toIndex = displayUnits.indexOf(to);
    // Special case handling for m3 <-> l
    if (from === 'm3' && to === 'l' || from === 'l' && to === 'm3') {
      isSpecialCase = true;
      isGoingDown = from === 'm3';
      result = isGoingDown ? value * 1000 : value / 1000;
    } else if (fromIndex !== -1 && toIndex !== -1) {
      isGoingDown = toIndex > fromIndex;
      const steps = Math.abs(toIndex - fromIndex);
      result = isGoingDown ? value * Math.pow(10, steps) : value / Math.pow(10, steps);
    }
  }
  const isHighlighted = (idx: number) => {
    if (fromIndex === -1 || toIndex === -1 || isSpecialCase) return unit === highlight;
    const startIdx = Math.min(fromIndex, toIndex);
    const endIdx = Math.max(fromIndex, toIndex);
    return idx >= startIdx && idx <= endIdx;
  };
  const isEndpoint = (idx: number) => {
    if (isSpecialCase) return false;
    return idx === fromIndex || idx === toIndex;
  };
  if (isSpecialCase && from && to && value !== undefined && result !== undefined) {
    return <div className="bg-gradient-to-br from-indigo-50 to-blue-50 rounded-xl p-4 border-2 border-indigo-200" data-id="element-12051">
        <div className="flex items-center justify-center gap-3 mb-3" data-id="element-12052">
          <div className="flex flex-col items-center bg-indigo-600 text-white rounded-lg px-4 py-2 border-2 border-indigo-700 shadow-md" data-id="element-12053">
            <span className="text-xl font-bold" data-id="element-12054">{from}</span>
          </div>
          <div className="flex flex-col items-center" data-id="element-12055">
            <ArrowRight className="w-6 h-6 text-indigo-600" data-id="element-12056" />
            <span className="text-xs font-bold text-indigo-700" data-id="element-12057">
              {isGoingDown ? '×1000' : '÷1000'}
            </span>
          </div>
          <div className="flex flex-col items-center bg-indigo-600 text-white rounded-lg px-4 py-2 border-2 border-indigo-700 shadow-md" data-id="element-12058">
            <span className="text-xl font-bold" data-id="element-12059">{to}</span>
          </div>
        </div>
        <div className="bg-white rounded-lg p-3 border border-indigo-200 text-center" data-id="element-12060">
          <p className="text-sm text-slate-700" data-id="element-12061">
            <span className="font-bold text-indigo-900" data-id="element-12062">
              {value} {from}
            </span>
            {' = '}
            <span className="font-bold text-emerald-700" data-id="element-12063">
              {result.toLocaleString()} {to}
            </span>
          </p>
        </div>
      </div>;
  }
  return <div className="bg-gradient-to-br from-indigo-50 to-blue-50 rounded-xl p-4 md:p-6 border-2 border-indigo-200 overflow-x-auto" data-id="element-12064">
      <h4 className="font-bold text-indigo-900 mb-6 text-center text-sm" data-id="element-12065">
        {type === 'length' && '📏 Lengtematen Trap'}
        {type === 'volume' && '🧃 Inhoudsmaten Trap'}
        {type === 'weight' && '⚖️ Gewichten Trap'}
      </h4>

      <div className="flex flex-col items-start min-w-[280px] max-w-md mx-auto relative pl-4 pb-4" data-id="element-12066">
        {displayUnits.map((unit, idx) => {
        const highlighted = isHighlighted(idx);
        const endpoint = isEndpoint(idx);
        const showArrow = idx < displayUnits.length - 1;
        const arrowHighlighted = fromIndex !== -1 && toIndex !== -1 && idx >= Math.min(fromIndex, toIndex) && idx < Math.max(fromIndex, toIndex);
        return <div key={unit} className="relative flex flex-col" style={{
          marginLeft: `${idx * 12}%`
        }} data-id="element-12067">
              {/* The Step */}
              <div className={`relative z-10 flex items-center justify-center w-16 h-10 rounded-lg font-bold text-sm border-2 shadow-sm transition-colors
                  ${endpoint ? 'bg-indigo-600 text-white border-indigo-700 ring-2 ring-indigo-200' : highlighted ? 'bg-indigo-100 text-indigo-800 border-indigo-300' : 'bg-white text-slate-600 border-slate-200'}`} data-id="element-12068">
                {unit}
              </div>

              {/* The Arrow to the next step */}
              {showArrow && <div className="relative h-8 w-16 left-8 -mt-1 -mb-1 z-0" data-id="element-12069">
                  <svg className="absolute inset-0 w-full h-full" preserveAspectRatio="none" viewBox="0 0 100 100" data-id="element-12070">
                    <path d="M 20 0 L 20 50 L 80 50 L 80 100" fill="none" stroke={arrowHighlighted ? '#4f46e5' : '#cbd5e1'} strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" data-id="element-12071" />
                    {/* Down arrow head */}
                    <path d="M 70 85 L 80 100 L 90 85" fill="none" stroke={arrowHighlighted && isGoingDown ? '#4f46e5' : '#cbd5e1'} strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" data-id="element-12072" />
                    {/* Up arrow head */}
                    <path d="M 10 15 L 20 0 L 30 15" fill="none" stroke={arrowHighlighted && !isGoingDown ? '#4f46e5' : '#cbd5e1'} strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" data-id="element-12073" />
                  </svg>
                  <div className={`absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white/80 px-1 rounded text-[10px] font-bold ${arrowHighlighted ? 'text-indigo-700' : 'text-slate-400'}`} data-id="element-12074">
                    {arrowHighlighted ? isGoingDown ? '×10' : '÷10' : '10'}
                  </div>
                </div>}
            </div>;
      })}
      </div>

      {from && to && result !== undefined && <div className="mt-6 bg-white rounded-lg p-4 border border-indigo-200 text-center shadow-sm max-w-sm mx-auto" data-id="element-12075">
          <p className="text-sm text-slate-700 mb-1" data-id="element-12076">
            Je gaat <strong data-id="element-12077">{Math.abs(toIndex - fromIndex)}</strong> treden naar{' '}
            {isGoingDown ? 'beneden (×)' : 'boven (÷)'}
          </p>
          <p className="text-base text-slate-800" data-id="element-12078">
            <span className="font-bold text-indigo-900" data-id="element-12079">
              {value} {from}
            </span>
            {' = '}
            <span className="font-bold text-emerald-700" data-id="element-12080">
              {result} {to}
            </span>
          </p>
        </div>}
    </div>;
}